import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LogoShield } from '@/components/LogoShield';
import { toast } from 'sonner';
import { z } from 'zod';
import { ServiceAgreementDialog } from '@/components/portal/ServiceAgreementDialog';
import { MfaVerification } from '@/components/portal/MfaVerification';
import { MfaEnrollment } from '@/components/portal/MfaEnrollment';
import { Helmet } from '@/components/Helmet';
import { type TierType } from '@/components/portal/TierSelection';

const emailSchema = z.string().email('Invalid email address');
const passwordSchema = z.string().min(6, 'Password must be at least 6 characters');

type MfaStep = 'none' | 'verify' | 'enroll';

export default function Auth() {
  const { user, signIn, signUp, loading, signOut, markSessionMfaVerified, isSessionMfaVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupTier, setSignupTier] = useState<TierType | null>(null);
  const [signupSource, setSignupSource] = useState<string | null>(null);

  // MFA state
  const [mfaStep, setMfaStep] = useState<MfaStep>('none');
  const [mfaMethod, setMfaMethod] = useState<'totp' | 'email' | null>(null);
  const [availableMfaMethods, setAvailableMfaMethods] = useState<string[]>([]);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const defaultTab = location.pathname.includes('sign-up') ? 'signup' : 'login';
  const pageTitle = defaultTab === 'signup'
    ? 'Create Portal Account | ShadowScan'
    : 'Portal Login | ShadowScan';
  const pageDescription =
    'Access the ShadowScan customer portal to manage protected domains, billing, and security alerts, or create your account to start monitored protection.';
  const redirectAfterAuth = typeof (location.state as any)?.from === 'string'
    ? (location.state as any).from
    : '/portal';

  // Track if MFA process has been initiated to prevent re-triggering
  const [mfaCheckDone, setMfaCheckDone] = useState(false);

  const sessionMfaVerified = isSessionMfaVerified();

  useEffect(() => {
    // If current session already verified, allow portal access
    if (sessionMfaVerified && user && !loading) {
      navigate(redirectAfterAuth, { replace: true });
      return;
    }

    // Only check MFA status once per login session
    if (user && !loading && mfaStep === 'none' && !mfaCheckDone) {
      setMfaCheckDone(true);
      checkMfaStatus(user.id, user.email || '');
    }
  }, [user, loading, mfaCheckDone, mfaStep, navigate, redirectAfterAuth, sessionMfaVerified]);

  // Reset MFA state when user signs out or session is cleared
  useEffect(() => {
    if (!user) {
      setMfaStep('none');
      setMfaMethod(null);
      setAvailableMfaMethods([]);
      setPendingUserId(null);
      setPendingEmail(null);
      setMfaCheckDone(false);
    }
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planParam = params.get('plan');
    const sourceParam = params.get('source');
    if (planParam === 'essential' || planParam === 'premium') {
      setSignupTier(planParam);
    } else {
      setSignupTier(null);
    }
    if (sourceParam && sourceParam.trim().length > 0) {
      setSignupSource(sourceParam.trim());
    } else {
      setSignupSource(null);
    }
  }, [location.search]);

  const ensureProfile = async (userId: string, email: string) => {
    // Ensure a profile row exists (older users may be missing)
    const { data, error } = await supabase
      .from('profiles')
      .select('user_id, mfa_method')
      .eq('user_id', userId)
      .maybeSingle();

    if (data) return data;

    // If not found, create a minimal profile row
    const { data: inserted, error: insertError } = await supabase
      .from('profiles')
      .insert({ user_id: userId, email })
      .select('user_id, mfa_method')
      .maybeSingle();

    if (insertError) {
      throw insertError;
    }
    return inserted;
  };

  const checkMfaStatus = async (userId: string, email: string) => {
    try {
      // Small delay to ensure session is fully established
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const profile = await ensureProfile(userId, email);

      if (profile?.mfa_method) {
        // Parse available methods
        const methods = profile.mfa_method.split(',').filter((m: string) => m.trim().length > 0);
        setAvailableMfaMethods(methods);
        
        // User has MFA enabled, require verification
        setPendingUserId(userId);
        setPendingEmail(email);
        
        // If only one method, use it directly. If multiple, default to first (usually totp)
        const preferredMethod = methods.includes('totp') ? 'totp' : methods[0] as 'totp' | 'email';
        setMfaMethod(preferredMethod);
        setMfaStep('verify');
      } else {
        // No MFA set up, prompt to enroll
        setPendingUserId(userId);
        setPendingEmail(email);
        setAvailableMfaMethods([]);
        setMfaStep('enroll');
      }
    } catch (err) {
      console.error('Error checking MFA status:', err);
      toast.error('Unable to verify MFA status. Please try signing in again.');
      await signOut();
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      emailSchema.parse(loginEmail);
      passwordSchema.parse(loginPassword);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
        setIsLoading(false);
        return;
      }
    }

    const { error } = await signIn(loginEmail, loginPassword);
    if (error) {
      toast.error(error.message);
      setIsLoading(false);
    } else {
      // Don't navigate yet - MFA check will happen via useEffect
      setIsLoading(false);
    }
  };

  const handleSignupClick = (e: React.FormEvent) => {
    e.preventDefault();

    try {
      emailSchema.parse(signupEmail);
      passwordSchema.parse(signupPassword);
      if (!signupName.trim()) throw new Error('Name is required');
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
      } else if (err instanceof Error) {
        toast.error(err.message);
      }
      return;
    }

    setShowAgreement(true);
  };

  const handleSignupConfirm = async () => {
    setShowAgreement(false);
    setIsLoading(true);

    const { error } = await signUp(signupEmail, signupPassword, signupName, signupSource);
    if (error) {
      if (error.message.includes('already registered')) {
        toast.error('An account with this email already exists');
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success('Account created! You can now log in.');
    }
    setIsLoading(false);
  };

  const handleAgreementDecline = () => {
    setShowAgreement(false);
    toast.info('You must accept the service agreement to create an account.');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      emailSchema.parse(forgotEmail);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
        setIsLoading(false);
        return;
      }
    }

    const redirectUrl = `${window.location.origin}/portal/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: redirectUrl,
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
      setForgotEmail('');
    }
    setIsLoading(false);
  };

  const handleMfaSuccess = async () => {
    // Refresh session so we have the latest access token and mark MFA for both token and user
    const { data } = await supabase.auth.getSession();
    markSessionMfaVerified(data?.session ?? null);
    toast.success('Logged in successfully');
    navigate(redirectAfterAuth, { replace: true });
  };

  const handleMfaCancel = async () => {
    // Sign out and reset state
    await signOut();
    setMfaStep('none');
    setMfaCheckDone(false);
    setPendingUserId(null);
    setPendingEmail(null);
    setMfaMethod(null);
  };

  const handleMfaEnrollmentComplete = (method: 'totp' | 'email') => {
    markSessionMfaVerified();
    toast.success('Two-factor authentication enabled!');
    // Use replace to prevent back button issues
    navigate(redirectAfterAuth, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  // Show MFA verification if needed
  if (mfaStep === 'verify' && pendingUserId && pendingEmail && mfaMethod) {
    const handleSwitchMethod = (method: 'totp' | 'email') => {
      setMfaMethod(method);
    };

    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <MfaVerification
          userId={pendingUserId}
          email={pendingEmail}
          mfaMethod={mfaMethod}
          availableMethods={availableMfaMethods}
        onSuccess={handleMfaSuccess}
          onCancel={handleMfaCancel}
          onSwitchMethod={availableMfaMethods.length > 1 ? handleSwitchMethod : undefined}
        />
      </>
    );
  }

  // Show MFA enrollment if user doesn't have MFA set up (mandatory - no skip option)
  if (mfaStep === 'enroll' && pendingUserId && pendingEmail) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <MfaEnrollment
          userId={pendingUserId}
          email={pendingEmail}
          onComplete={handleMfaEnrollmentComplete}
          showBackButton
          onBack={handleMfaCancel}
        />
      </>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Helmet>
          <title>Reset Portal Password | ShadowScan</title>
          <meta
            name="description"
            content="Reset your ShadowScan portal password and regain access to your protected domains with a secure recovery link."
          />
        </Helmet>
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <Card className="w-full max-w-md cyber-card relative z-10">
          <Link 
            to="/" 
            className="absolute top-4 left-4 p-2 text-primary/70 hover:text-primary border border-primary/30 hover:border-primary/60 rounded transition-all hover:shadow-[0_0_15px_hsl(185_80%_50%/0.3)]"
            aria-label="Back to Home"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <CardHeader className="text-center pt-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <LogoShield className="h-8 w-8" />
              <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
            </div>
            <CardTitle className="text-xl">Reset Password</CardTitle>
            <CardDescription>Enter your email to receive a password reset link</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-muted/50"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setShowForgotPassword(false)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <Card className="w-full max-w-md cyber-card relative z-10">
        <Link 
          to="/" 
          className="absolute top-4 left-4 p-2 text-primary/70 hover:text-primary border border-primary/30 hover:border-primary/60 rounded transition-all hover:shadow-[0_0_15px_hsl(185_80%_50%/0.3)]"
          aria-label="Back to Home"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <CardHeader className="text-center pt-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <LogoShield className="h-8 w-8" />
            <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
          </div>
          <CardTitle className="text-xl">Customer Portal</CardTitle>
          <CardDescription>Manage your protected domains</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="bg-muted/50 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignupClick} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Full Name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="bg-muted/50 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ServiceAgreementDialog
        open={showAgreement}
        onAccept={handleSignupConfirm}
        onDecline={handleAgreementDecline}
        clientName={signupName}
        clientEmail={signupEmail}
        selectedTier={signupTier || undefined}
      />
    </div>
  );
}
